/**
 * Drag (and Alt+Arrow) reordering for channels and roles in Server
 * settings. Pure helpers plus one hook that wires HTML drag-and-drop; the
 * server checks the hierarchy and refuses a reorder as a whole (§4.2).
 */
import type { Role, Snowflake } from '@neosocial/protocol';
import { useState, type DragEvent, type KeyboardEvent } from 'react';

/** Moves the item at `from` to index `to`. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = list.slice();
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out;
  const [it] = out.splice(from, 1);
  out.splice(to, 0, it as T);
  return out;
}

/**
 * Role positions for a top-to-bottom order (highest first), as
 * `PATCH /guilds/{id}/roles` takes them: the bottom role gets 1 (@everyone
 * stays 0). Only roles whose position changes are sent.
 */
export function rolePositions(
  orderTopFirst: readonly Snowflake[],
  roles: readonly Pick<Role, 'id' | 'position'>[],
): Array<{ id: Snowflake; position: number }> {
  const n = orderTopFirst.length;
  const out: Array<{ id: Snowflake; position: number }> = [];
  orderTopFirst.forEach((id, i) => {
    const position = n - i;
    if (roles.find((r) => r.id === id)?.position !== position) out.push({ id, position });
  });
  return out;
}

/** Channel positions for siblings in display order: 0, 1, 2… (only changes). */
export function channelPositions(
  order: readonly Snowflake[],
  current: Record<Snowflake, number | undefined>,
): Array<{ id: Snowflake; position: number }> {
  return order
    .map((id, position) => ({ id, position }))
    .filter((x) => current[x.id] !== x.position);
}

export interface DragProps {
  draggable: boolean;
  onDragStart(e: DragEvent): void;
  onDragOver(e: DragEvent): void;
  onDrop(e: DragEvent): void;
  onDragEnd(): void;
  onKeyDown(e: KeyboardEvent): void;
  'data-drop'?: 'true';
}

/**
 * Drag props for item `i` of `ids`. `canMove(id)` limits which items move;
 * `group(id)` keeps drags inside one group (a category's channels).
 */
export function useDragOrder(
  ids: readonly Snowflake[],
  onReorder: (next: Snowflake[]) => void,
  canMove: (id: Snowflake) => boolean,
  group: (id: Snowflake) => string = () => '',
): (i: number) => DragProps {
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const ok = (from: number, to: number) => {
    const a = ids[from];
    const b = ids[to];
    return !!a && !!b && canMove(a) && canMove(b) && group(a) === group(b);
  };
  return (i: number) => ({
    draggable: canMove(ids[i] ?? ''),
    onDragStart: (e: DragEvent) => {
      setDrag(i);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(i));
    },
    onDragOver: (e: DragEvent) => {
      if (drag === null || !ok(drag, i)) return;
      e.preventDefault();
      if (over !== i) setOver(i);
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (drag !== null && ok(drag, i) && drag !== i) onReorder(moveItem(ids, drag, i));
      setDrag(null);
      setOver(null);
    },
    onDragEnd: () => {
      setDrag(null);
      setOver(null);
    },
    // keyboard: Alt+ArrowUp / Alt+ArrowDown moves the focused item
    onKeyDown: (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      const to = e.key === 'ArrowUp' ? i - 1 : i + 1;
      if (!ok(i, to)) return;
      e.preventDefault();
      onReorder(moveItem(ids, i, to));
    },
    ...(over === i && drag !== i ? { 'data-drop': 'true' as const } : {}),
  });
}
